import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/PageHeader";
import { ProjectForm } from "@/features/projects/ProjectForm";
import { api } from "@/lib/api";
import type { Project, ProjectCreateInput } from "@axorial/shared";

interface ProjectDetailMin {
  project: Project;
}

export const Route = createFileRoute("/projects/$projectId/edit")({
  component: EditProjectPage,
});

function EditProjectPage() {
  const { projectId } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery<ProjectDetailMin>({
    queryKey: ["project", projectId],
    queryFn: () => api.get(`/projects/${projectId}`),
  });

  const update = useMutation({
    mutationFn: (input: ProjectCreateInput) =>
      api.patch<{ ok: true }>(`/projects/${projectId}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["project", projectId] });
      navigate({ to: "/projects/$projectId", params: { projectId } });
    },
  });

  if (isLoading || !data) {
    return (
      <div className="grid place-items-center py-20">
        <span className="loading loading-spinner loading-lg text-primary" />
      </div>
    );
  }

  const { project } = data;

  return (
    <div className="space-y-6 max-w-2xl">
      <PageHeader
        eyebrow="editar proyecto"
        title={project.name}
        subtitle="Cambia metadata, integraciones y descripción. El slug es inmutable."
      />
      <ProjectForm
        mode="edit"
        defaultValues={{
          name: project.name,
          slug: project.slug,
          type: project.type,
          status: project.status,
          health: project.health,
          description: project.description ?? undefined,
          planeProjectId: project.planeProjectId ?? undefined,
          planeWorkspaceSlug: project.planeWorkspaceSlug ?? undefined,
          mattermostChannelId: project.mattermostChannelId ?? undefined,
        }}
        onSubmit={(input) => update.mutate(input)}
        onCancel={() => navigate({ to: "/projects/$projectId", params: { projectId } })}
        submitting={update.isPending}
      />
    </div>
  );
}
